import { LineArrowLeft } from '@qr-front/icons/line-arrow-left'
import { LineClose } from '@qr-front/icons/line-close'
import { Save } from '@qr-front/icons/save'
import { Button, buttonCls } from '@qr-front/ui/components/Button'
import { container } from '@qr-front/ui/patterns'
import { cn } from '@qr-front/ui/utils'
import { useAtomValue } from 'jotai'
import { useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useRef } from 'react'
import { useBus } from 'react-bus'

import { ROUTES } from '@/constants'
import { Link } from '@/i18n-routing'

import { qrTypeAtom } from '../../generatorState'
import { QR_TYPE_TO_PARAM } from '../../generatorTypes'
import { useCurrGeneratorStep } from '../../hooks'
import { EditModeGeneratorStepper } from '../GeneratorSteppers'
import { NextButton } from './NextButton'
import { PreviewButton } from './PreviewButton'

export const ATTEMPT_SAVE_EDITED_CONTENT_FORM_EVENT =
	'ATTEMPT_SAVE_EDITED_CONTENT_FORM_EVENT'

const SaveButton = ({ onSave }: { onSave: () => void }) => {
	const saveButtonRef = useRef<HTMLButtonElement>(null)

	return (
		<>
			<Button
				ref={saveButtonRef}
				visual="soft"
				size="icon-large"
				className="shrink-0"
				onClick={onSave}
			>
				<Save />
			</Button>
		</>
	)
}

const MobileView = ({
	backUrl,
	onSave,
}: {
	backUrl: string
	onSave: () => void
}) => {
	const isDesignStep = useCurrGeneratorStep() === 'design'

	const closeButton = (
		<Link
			href={ROUTES.cabinet.qrCodes}
			className={cn(
				buttonCls({ visual: 'soft', size: 'icon-large' }),
				'text-grey hover:text-blue',
			)}
		>
			<LineClose className="size-4" />
		</Link>
	)

	const backButton = (
		<Link
			href={backUrl}
			className={cn(
				buttonCls({ visual: 'secondary', size: 'icon-large' }),
				'shrink-0',
			)}
		>
			<LineArrowLeft className="size-4" />
		</Link>
	)

	return (
		<div
			className={cn(
				container,
				'flex h-20 items-center justify-between gap-4 desktop:hidden',
			)}
		>
			<div className="flex shrink-0 gap-4">
				{closeButton}
				<PreviewButton />
				{isDesignStep ? backButton : <SaveButton onSave={onSave} />}
			</div>
			<NextButton editMode className="min-w-0 shrink grow" />
		</div>
	)
}
const DesktopView = ({
	backUrl,
	onSave,
}: {
	backUrl: string
	onSave: () => void
}) => {
	const t = useTranslations()

	const isDesignStep = useCurrGeneratorStep() === 'design'

	const backButton = (
		<Link
			href={backUrl}
			className={buttonCls({ visual: 'secondary', size: 'large' })}
		>
			<LineArrowLeft className="size-4" />
			{t('generator__footer__back_button')}
		</Link>
	)

	return (
		<div
			className={cn(
				container,
				'hidden h-20 items-center gap-x-8 desktop:grid desktop:grid-cols-[1fr_minmax(280px,max-content)]',
			)}
		>
			<div className="flex min-w-0 items-center justify-between">
				<Link
					href={ROUTES.cabinet.qrCodes}
					className={cn(
						buttonCls({ visual: 'soft', size: 'large' }),
						'type-body-medium px-4 font-normal',
					)}
				>
					{t('generator__footer__edit_mode__exit_button')}
				</Link>
				<EditModeGeneratorStepper className="only:ml-auto" />
			</div>
			<div
				className={cn(
					'flex justify-center',
					isDesignStep ? 'gap-2' : 'gap-4',
				)}
			>
				{isDesignStep ? backButton : <SaveButton onSave={onSave} />}
				<NextButton editMode className="grow" />
			</div>
		</div>
	)
}

export const EditModeGeneratorFooter = () => {
	const searchParams = useSearchParams()
	const bus = useBus()

	const qrType = useAtomValue(qrTypeAtom)

	const backUrl = `${ROUTES.generator.typeStep}/${QR_TYPE_TO_PARAM[qrType!]}?${searchParams}`

	const handleSave = async () => {
		bus.emit(ATTEMPT_SAVE_EDITED_CONTENT_FORM_EVENT)
	}

	return (
		<>
			<MobileView backUrl={backUrl} onSave={handleSave} />
			<DesktopView backUrl={backUrl} onSave={handleSave} />
		</>
	)
}
